import * as THREE from 'three';
import { PLAYER_CONFIG } from './config.js';

export class Player {
  constructor(id, username, color, isLocal = false) {
    this.id = id;
    this.username = username;
    this.color = color;
    this.isLocal = isLocal;
    this.group = new THREE.Group();
    
    this.mesh = null;
    this.nameTag = null;
    
    this.position = new THREE.Vector3(
      Math.random() * 10 - 5,
      PLAYER_CONFIG.size / 2,
      Math.random() * 10 - 5
    );

    this.targetPosition = this.position.clone();
    this.velocity = new THREE.Vector3();
    this.speed = PLAYER_CONFIG.speed;

    this.createPlayer();
  }

  createPlayer() {
    // Cria o quadrado (cubo achatado)
    const geometry = new THREE.BoxGeometry(
      PLAYER_CONFIG.size,
      PLAYER_CONFIG.size,
      PLAYER_CONFIG.size
    );
    
    const material = new THREE.MeshStandardMaterial({
      color: this.color,
      roughness: 0.5,
      metalness: 0.3
    });

    this.mesh = new THREE.Mesh(geometry, material);
    this.mesh.castShadow = true;
    this.mesh.receiveShadow = true;
    this.group.add(this.mesh);

    // Cria a tag com o nome
    this.createNameTag();

    // Posiciona o grupo
    this.group.position.copy(this.position);
  }

  createNameTag() {
    // Cria um canvas para o texto
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    
    // Configura o tamanho do canvas
    canvas.width = 512;
    canvas.height = 128;

    // Estilo do texto
    context.font = `bold ${PLAYER_CONFIG.nameTag.fontSize}px ${PLAYER_CONFIG.nameTag.fontFamily}`;
    context.fillStyle = PLAYER_CONFIG.nameTag.backgroundColor;
    context.textAlign = 'center';
    context.textBaseline = 'middle';

    // Desenha o fundo
    const textWidth = context.measureText(this.username).width;
    const padding = PLAYER_CONFIG.nameTag.padding;
    const backgroundWidth = textWidth + padding * 2;
    const backgroundHeight = PLAYER_CONFIG.nameTag.fontSize + padding * 2;
    
    const x = canvas.width / 2;
    const y = canvas.height / 2;
    
    context.fillStyle = PLAYER_CONFIG.nameTag.backgroundColor;
    context.fillRect(
      x - backgroundWidth / 2,
      y - backgroundHeight / 2,
      backgroundWidth,
      backgroundHeight
    );

    // Desenha o texto
    context.fillStyle = PLAYER_CONFIG.nameTag.color;
    context.fillText(this.username, x, y);

    // Cria a textura do canvas
    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;

    // Cria um sprite com a textura
    const spriteMaterial = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      depthTest: false,
      depthWrite: false
    });

    this.nameTag = new THREE.Sprite(spriteMaterial);
    this.nameTag.scale.set(2, 0.5, 1);
    this.nameTag.position.y = PLAYER_CONFIG.size / 2 + PLAYER_CONFIG.nameTag.height;
    
    this.group.add(this.nameTag);
  }

  setPosition(x, y, z) {
    this.position.set(x, y, z);
    this.targetPosition.set(x, y, z);
    this.group.position.copy(this.position);
  }

  updatePosition(x, y, z) {
    // Atualiza a posição com interpolação suave (para jogadores remotos)
    this.targetPosition.set(x, y, z);
  }

  move(direction) {
    // Movimento local (apenas para o jogador local)
    if (!this.isLocal) return;

    const movement = new THREE.Vector3();
    
    if (direction === 'forward') movement.z = -this.speed;
    if (direction === 'backward') movement.z = this.speed;
    if (direction === 'left') movement.x = -this.speed;
    if (direction === 'right') movement.x = this.speed;

    this.position.add(movement);
    this.targetPosition.copy(this.position);
  }

  update(deltaTime) {
    // Interpolação suave para jogadores remotos
    if (!this.isLocal) {
      this.position.lerp(this.targetPosition, 0.1);
    }

    // Mantém a posição Y constante (no plano)
    this.position.y = PLAYER_CONFIG.size / 2;
    
    // Atualiza a posição do grupo
    this.group.position.copy(this.position);
  }

  getGroup() {
    return this.group;
  }

  dispose() {
    // Limpa os recursos
    if (this.mesh) {
      this.mesh.geometry.dispose();
      this.mesh.material.dispose();
    }
    
    if (this.nameTag && this.nameTag.material.map) {
      this.nameTag.material.map.dispose();
      this.nameTag.material.dispose();
    }
  }
}

export class PlayerManager {
  constructor(scene) {
    this.scene = scene;
    this.players = new Map();
    this.colorIndex = 0;
    this.localPlayer = null;
    this.keys = { w: false, a: false, s: false, d: false };
    
    this.setupKeyboardControls();
  }

  setupKeyboardControls() {
    window.addEventListener('keydown', (e) => {
      const key = e.key.toLowerCase();
      if (key === 'w' || key === 'a' || key === 's' || key === 'd') {
        this.keys[key] = true;
        e.preventDefault();
      }
    });

    window.addEventListener('keyup', (e) => {
      const key = e.key.toLowerCase();
      if (key === 'w' || key === 'a' || key === 's' || key === 'd') {
        this.keys[key] = false;
        e.preventDefault();
      }
    });
  }

  update(deltaTime) {
    // Atualiza movimento do jogador local
    if (this.localPlayer) {
      if (this.keys.w) this.localPlayer.move('forward');
      if (this.keys.s) this.localPlayer.move('backward');
      if (this.keys.a) this.localPlayer.move('left');
      if (this.keys.d) this.localPlayer.move('right');
    }

    // Atualiza todos os jogadores
    this.players.forEach(player => {
      player.update(deltaTime);
    });
  }

  getLocalPlayerPosition() {
    if (this.localPlayer) {
      return {
        x: this.localPlayer.position.x,
        y: this.localPlayer.position.y,
        z: this.localPlayer.position.z
      };
    }
    return null;
  }

  addPlayer(id, username, isLocal = false) {
    if (this.players.has(id)) {
      console.warn(`Jogador ${id} já existe`);
      return this.players.get(id);
    }

    // Seleciona uma cor do array de cores
    const color = PLAYER_CONFIG.colors[this.colorIndex % PLAYER_CONFIG.colors.length];
    this.colorIndex++;

    // Cria o jogador
    const player = new Player(id, username, color, isLocal);
    this.players.set(id, player);
    
    if (isLocal) {
      this.localPlayer = player;
    }

    // Adiciona à cena
    this.scene.addObject(player.getGroup());

    console.log(`Jogador adicionado: ${username} (${id})`);
    return player;
  }

  removePlayer(id) {
    const player = this.players.get(id);
    if (!player) {
      console.warn(`Jogador ${id} não encontrado`);
      return;
    }

    // Remove da cena
    this.scene.removeObject(player.getGroup());

    // Limpa recursos
    player.dispose();

    // Remove do mapa
    this.players.delete(id);

    console.log(`Jogador removido: ${id}`);
  }

  getPlayer(id) {
    return this.players.get(id);
  }

  getAllPlayers() {
    return Array.from(this.players.values());
  }

  getPlayerCount() {
    return this.players.size;
  }

  updatePlayerPosition(id, x, y, z) {
    const player = this.players.get(id);
    if (player) {
      player.updatePosition(x, y, z);
    }
  }

  clear() {
    this.players.forEach(player => {
      this.scene.removeObject(player.getGroup());
      player.dispose();
    });
    this.players.clear();
  }
}
